import { useRef, useState } from "react";
import type { LoginClaimResult } from "src/domains/shared/api/exclusiveLogin/exclusiveLogin.model";
import { claimLogin } from "src/domains/shared/api/exclusiveLogin/exclusiveLogin.repository";
import { clearLoginClaimPending } from "src/domains/shared/utils/exclusiveLogin/loginClaimPending";
import { writeLoginTabSignal } from "src/domains/shared/utils/exclusiveLogin/loginTabSignal";
import { signOutThisBrowser } from "src/domains/shared/utils/exclusiveLogin/signOutThisBrowser";
import { hasCognitoSession } from "src/domains/shared/utils/injectCognitoSession";

/** 점유가 확인되지 않아 로그인이 끝난 사유 — 밀려남(다시 로그인 안내)·일시 장애(재시도 안내) */
export type LoginClaimFailure = "superseded" | "unavailable";

/**
 * 점유 실패 사유 → 안내 문구 키(`auth` 네임스페이스). 비밀번호 로그인과 SSO 콜백이 같은 문구로 안내한다(스펙
 * 「확인되지 않은 점유는 로그인을 끝낸다」).
 */
export const LOGIN_CLAIM_FAILURE_MESSAGE_KEY = {
  superseded: "message.claimSuperseded",
  unavailable: "message.claimUnavailable",
} as const satisfies Record<LoginClaimFailure, string>;

export interface LoginClaimFlowOptions {
  /** SSO 콜백이 주입한 ID 토큰. 주면 이 토큰을 직접 실어 점유하고, 없으면 현재 세션의 토큰을 쓴다 */
  idToken?: string;
  /** 점유가 진행으로 끝났다 — 로그인을 마친다(화면 이동 등) */
  onClaimed: () => void | Promise<void>;
  /** 점유가 확인되지 않아 이 브라우저에서 로그아웃한 뒤 불린다 */
  onFailed: (reason: LoginClaimFailure) => void;
  /** 확인창에서 취소해 이 브라우저에서 로그아웃한 뒤 불린다 */
  onCancelled: () => void;
}

/**
 * 로그인 직후 점유 흐름 (openspec `exclusive-login`, design D3·D4).
 *
 * 생산자(비밀번호 로그인·SSO 콜백)는 점유 대기 표식을 쓰고 토큰을 저장한 뒤 `claim()` 을 부른다.
 * - 진행: 표식을 지우고 탭 신호 `signed-in` 을 쓴 뒤 `onClaimed`.
 * - 다른 기기 사용 중: 확인창을 연다. 표식은 사용자가 고를 때까지 남는다.
 * - 밀려남·일시 장애: 이 브라우저에서만 로그아웃하고 표식을 지운 뒤 `onFailed`.
 *
 * 확인창의 "로그인"(`confirm`)은 `force` 재점유이고, "취소"(`cancel`)는 해제·전역 로그아웃 없이
 * 이 브라우저에서만 로그아웃한다. 확인 중에는 결과가 확정될 때까지 확인창을 닫지 않고(취소는 고른 즉시
 * 닫는다), 확인창을 닫은 뒤에야 콜백이 화면을 옮긴다.
 */
export const useLoginClaimFlow = ({
  idToken,
  onClaimed,
  onFailed,
  onCancelled,
}: LoginClaimFlowOptions) => {
  const [conflictOpen, setConflictOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // 확인·취소는 하나만 진행한다. 같은 틱의 연속 입력은 상태가 반영되기 전이라 ref 로 잠근다.
  const choosingRef = useRef(false);

  const completeClaim = async () => {
    clearLoginClaimPending();
    writeLoginTabSignal("signed-in");
    setConflictOpen(false);
    await onClaimed();
  };

  const endLogin = async (reason: LoginClaimFailure) => {
    await signOutThisBrowser();
    clearLoginClaimPending();
    setConflictOpen(false);
    onFailed(reason);
  };

  const settle = (
    result: LoginClaimResult,
    { forced }: { forced: boolean }
  ): Promise<void> => {
    switch (result) {
      case "claimed":
        return completeClaim();
      case "inUseElsewhere":
        // force 재점유가 다시 409 를 받는 것은 계약상 불가능하다 — 일시 장애와 같은 실패로 다룬다.
        if (forced) return endLogin("unavailable");
        setConflictOpen(true);
        return Promise.resolve();
      case "superseded":
      case "unavailable":
        return endLogin(result);
    }
  };

  /**
   * 점유한다. 기본은 `force=false` 다. 자리가 이미 이 브라우저 것임을 확인한 경우(SSO 의 같은 브라우저
   * 재진입)에만 `force: true` 로 확인창 없이 이어받는다.
   */
  const claim = async ({ force = false }: { force?: boolean } = {}) => {
    await settle(await claimLogin({ force, idToken }), { forced: force });
  };

  const confirm = async () => {
    if (choosingRef.current) return;
    choosingRef.current = true;
    setConfirming(true);
    try {
      // 확인창이 떠 있는 동안 다른 탭의 부팅이 이 로그인을 취소했을 수 있다(세션 키 없음).
      // 그때 재점유하면 이 브라우저는 로그아웃 상태인 채 다른 기기만 밀어낸다 — 실패로 끝낸다.
      if (!hasCognitoSession()) {
        await endLogin("unavailable");
        return;
      }
      await settle(await claimLogin({ force: true, idToken }), { forced: true });
    } finally {
      choosingRef.current = false;
      setConfirming(false);
    }
  };

  const cancel = async () => {
    if (choosingRef.current) return;
    choosingRef.current = true;
    setConflictOpen(false);
    try {
      await signOutThisBrowser();
      clearLoginClaimPending();
      onCancelled();
    } finally {
      choosingRef.current = false;
    }
  };

  return { conflictOpen, confirming, claim, confirm, cancel };
};
